import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'

/**
 * Estado de um mini tutorial individual
 */
export interface MiniTutorialState {
  seen: boolean      // Usuário já viu este mini tutorial?
  enabled: boolean   // Mini tutorial está habilitado para aparecer?
}

/**
 * Mapa completo de estados de todos os mini tutoriais
 */
export interface MiniTutorialsState {
  [key: string]: MiniTutorialState
}

/**
 * Lista de todas as chaves de mini tutoriais
 */
export const MINI_TUTORIAL_KEYS = [
  'dashboardSummary',
  'footer-remedies',
  'footer-profile',
  'footer-sos',
  'member-points-history',
  'member-adherence',
  'member-quiz',
  'member-streak-treatment',
  'member-streak-allopathic',
  'member-streak-illness',
  'member-badges-button',
  'member-badges-card',
  'member-today-homeopathies',
  'member-treatments-history',
  'member-medical-record',
  'member-health-diary',
  'member-reminders-log',
] as const

export type MiniTutorialKey = typeof MINI_TUTORIAL_KEYS[number]

/**
 * Service para gerenciar persistência de mini tutoriais
 */
class MiniTutorialService {
  
  /**
   * Busca o estado de todos os mini tutoriais do usuário
   */
  async getUserState(userId: string): Promise<MiniTutorialsState> {
    try {
      const { data, error } = await supabase
        .from('tutorial_states')
        .select('mini_tutorials_state')
        .eq('user_id', userId)
        .eq('tutorial_id', 'first_run')
        .single()

      if (error) {
        console.error('[MiniTutorialService] Error fetching state:', error)
        return this.getDefaultState()
      }

      return (data?.mini_tutorials_state || this.getDefaultState()) as MiniTutorialsState
    } catch (err) {
      console.error('[MiniTutorialService] Exception fetching state:', err)
      return this.getDefaultState()
    }
  }

  /**
   * Retorna estado padrão (todos habilitados, nenhum visto)
   */
  getDefaultState(): MiniTutorialsState {
    const state: MiniTutorialsState = {}
    MINI_TUTORIAL_KEYS.forEach((key) => {
      state[key] = { seen: false, enabled: true }
    })
    return state
  }

  /**
   * Verifica se um mini tutorial deve ser mostrado
   * Lógica: enabled === true && seen === false
   */
  shouldShow(key: string, state: MiniTutorialsState): boolean {
    const tutorialState = state[key]
    if (!tutorialState) return true // Se não existe, mostrar por padrão
    return tutorialState.enabled === true && tutorialState.seen === false
  }

  /**
   * Marca um mini tutorial como visto e desabilita
   * (Usuário clicou "Entendi" no mini tutorial)
   */
  async markAsSeen(userId: string, key: string): Promise<boolean> {
    try {
      logger.sensitive(`Marking ${key} as seen for user`, { userId, key })

      // Buscar estado atual
      const currentState = await this.getUserState(userId)

      // Atualizar estado: visto = true, habilitado = false
      const updatedState = {
        ...currentState,
        [key]: { seen: true, enabled: false }
      }

      // Salvar no banco
      const { error } = await supabase
        .from('tutorial_states')
        .update({ mini_tutorials_state: updatedState })
        .eq('user_id', userId)
        .eq('tutorial_id', 'first_run')

      if (error) {
        console.error('[MiniTutorialService] Error marking as seen:', error)
        return false
      }

      // Também atualizar localStorage (sincronização)
      this.updateLocalStorage(key, true)

      console.log(`[MiniTutorialService] ✅ Marked ${key} as seen`)
      return true
    } catch (err) {
      console.error('[MiniTutorialService] Exception marking as seen:', err)
      return false
    }
  }

  /**
   * Habilita um mini tutorial específico (botão cinza → verde)
   */
  async enableMiniTutorial(userId: string, key: string): Promise<boolean> {
    try {
      logger.sensitive(`Enabling ${key} for user`, { userId, key })

      const currentState = await this.getUserState(userId)

      // Atualizar estado: manter seen, mas habilitar
      const currentTutorialState = currentState[key] || { seen: false, enabled: false }
      const updatedState = {
        ...currentState,
        [key]: { ...currentTutorialState, enabled: true }
      }

      const { error } = await supabase
        .from('tutorial_states')
        .update({ mini_tutorials_state: updatedState })
        .eq('user_id', userId)
        .eq('tutorial_id', 'first_run')

      if (error) {
        console.error('[MiniTutorialService] Error enabling:', error)
        return false
      }

      // Remover do localStorage para que possa aparecer novamente
      this.updateLocalStorage(key, false)

      console.log(`[MiniTutorialService] ✅ Enabled ${key}`)
      return true
    } catch (err) {
      console.error('[MiniTutorialService] Exception enabling:', err)
      return false
    }
  }

  /**
   * Desabilita um mini tutorial específico (botão verde → cinza)
   */
  async disableMiniTutorial(userId: string, key: string): Promise<boolean> {
    try {
      logger.sensitive(`Disabling ${key} for user`, { userId, key })

      const currentState = await this.getUserState(userId)

      // Atualizar estado: desabilitar
      const currentTutorialState = currentState[key] || { seen: false, enabled: true }
      const updatedState = {
        ...currentState,
        [key]: { ...currentTutorialState, enabled: false }
      }

      const { error } = await supabase
        .from('tutorial_states')
        .update({ mini_tutorials_state: updatedState })
        .eq('user_id', userId)
        .eq('tutorial_id', 'first_run')

      if (error) {
        console.error('[MiniTutorialService] Error disabling:', error)
        return false
      }

      // Marcar no localStorage como visto
      this.updateLocalStorage(key, true)

      console.log(`[MiniTutorialService] ✅ Disabled ${key}`)
      return true
    } catch (err) {
      console.error('[MiniTutorialService] Exception disabling:', err)
      return false
    }
  }

  /**
   * Reseta todos os mini tutoriais (botão "Resetar Mini Tutoriais")
   */
  async resetAll(userId: string): Promise<boolean> {
    try {
      logger.sensitive(`Resetting all mini tutorials for user`, { userId })

      // Estado padrão: todos habilitados, nenhum visto
      const resetState = this.getDefaultState()

      const { error } = await supabase
        .from('tutorial_states')
        .update({ mini_tutorials_state: resetState })
        .eq('user_id', userId)
        .eq('tutorial_id', 'first_run')

      if (error) {
        console.error('[MiniTutorialService] Error resetting:', error)
        return false
      }

      // Limpar todo o localStorage de mini tutoriais
      this.clearAllLocalStorage()

      console.log('[MiniTutorialService] ✅ Reset all mini tutorials')
      return true
    } catch (err) {
      console.error('[MiniTutorialService] Exception resetting:', err)
      return false
    }
  }

  /**
   * Toggle: habilita se desabilitado, desabilita se habilitado
   */
  async toggleMiniTutorial(userId: string, key: string): Promise<boolean> {
    const currentState = await this.getUserState(userId)
    const tutorialState = currentState[key]
    
    if (!tutorialState || tutorialState.enabled === false) {
      return this.enableMiniTutorial(userId, key)
    } else {
      return this.disableMiniTutorial(userId, key)
    }
  }

  /**
   * Sincroniza estado do banco com localStorage
   * Chamado ao carregar o app
   */
  async syncWithLocalStorage(userId: string): Promise<void> {
    try {
      const state = await this.getUserState(userId)
      
      // Para cada mini tutorial, sincronizar localStorage
      Object.entries(state).forEach(([key, tutorialState]) => {
        if (tutorialState.seen || !tutorialState.enabled) {
          this.updateLocalStorage(key, true)
        } else {
          this.updateLocalStorage(key, false)
        }
      })

      console.log('[MiniTutorialService] ✅ Synced with localStorage')
    } catch (err) {
      console.error('[MiniTutorialService] Exception syncing:', err)
    }
  }

  /**
   * Atualiza localStorage para um mini tutorial específico
   */
  private updateLocalStorage(key: string, seen: boolean): void {
    try {
      if (seen) {
        localStorage.setItem(`minitip:${key}:seen`, '1')
      } else {
        localStorage.removeItem(`minitip:${key}:seen`)
      }
    } catch (err) {
      console.error('[MiniTutorialService] Error updating localStorage:', err)
    }
  }

  /**
   * Limpa todos os mini tutoriais do localStorage
   */
  private clearAllLocalStorage(): void {
    try {
      MINI_TUTORIAL_KEYS.forEach((key) => {
        localStorage.removeItem(`minitip:${key}:seen`)
      })
    } catch (err) {
      console.error('[MiniTutorialService] Error clearing localStorage:', err)
    }
  }
}

// Exportar instância singleton
export const miniTutorialService = new MiniTutorialService()
